import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardShell from '@/components/DashboardShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  User, Bell, Shield, Lock, Globe, Download, Trash2,
  Loader2, Check, ChevronRight, Mail, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Settings Section Component
const SettingsSection = ({ title, description, children }) => (
  <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden mb-6">
    <div className="px-6 py-4 border-b border-zinc-100">
      <h2 className="text-lg font-semibold text-zinc-900">{title}</h2>
      {description && <p className="text-sm text-zinc-500 mt-1">{description}</p>}
    </div>
    <div className="px-6 py-4 space-y-4">
      {children}
    </div>
  </div>
);

// Settings Row Component
const SettingsRow = ({ icon: Icon, label, description, children }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-5 h-5 text-zinc-400 mt-0.5" />}
      <div>
        <p className="font-medium text-zinc-900">{label}</p>
        {description && <p className="text-sm text-zinc-500">{description}</p>}
      </div>
    </div>
    <div className="flex-shrink-0 ml-4">
      {children}
    </div>
  </div>
);

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Data export/delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      let updateData = {};
      
      // Handle nested notification settings
      if (key.startsWith('notifications.')) {
        const notifKey = key.replace('notifications.', '');
        updateData = {
          notifications: {
            ...settings.notifications,
            [notifKey]: value
          }
        };
      } else {
        updateData = { [key]: value };
      }
      
      await axios.patch(`${API_URL}/settings`, updateData);
      
      // Update local state
      if (key.startsWith('notifications.')) {
        const notifKey = key.replace('notifications.', '');
        setSettings(prev => ({
          ...prev,
          notifications: {
            ...prev.notifications,
            [notifKey]: value
          }
        }));
      } else {
        setSettings(prev => ({ ...prev, [key]: value }));
      }
      
      toast.success('Setting updated');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDataExport = async () => {
    toast.info('Preparing your data export...');
    // In production, this would trigger a background job and email the user
    setTimeout(() => {
      toast.success('Data export requested. You will receive an email when ready.');
    }, 1000);
  };

  const handleAccountDeletion = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    toast.info('Processing account deletion request...');
    // In production, this would trigger a deletion workflow
    setTimeout(() => {
      toast.success('Account deletion requested. You will receive a confirmation email.');
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }, 1000);
  };

  if (loading) {
    return (
      <DashboardShell>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold text-zinc-900 mb-6">Settings</h1>

          {/* Account Settings */}
          <SettingsSection title="Account" description="Manage your account details">
            <SettingsRow icon={User} label="Name" description={user?.name}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/profile')}
              >
                Edit
              </Button>
            </SettingsRow>
            
            <SettingsRow icon={Mail} label="Email" description={user?.email}>
              <span className="text-sm text-zinc-500">Contact support to change</span>
            </SettingsRow>
            
            <SettingsRow icon={Lock} label="Password" description="Last changed: Unknown">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordModal(true)}
                data-testid="change-password-btn"
              >
                Change
              </Button>
            </SettingsRow>
          </SettingsSection>

          {/* Notification Settings */}
          <SettingsSection title="Notifications" description="Control how you receive updates">
            <SettingsRow 
              icon={Bell} 
              label="Job Updates" 
              description="Status changes, contractor arrivals"
            >
              <Switch
                checked={settings?.notifications?.job_updates ?? true}
                onCheckedChange={(checked) => updateSetting('notifications.job_updates', checked)}
                data-testid="notif-job-updates"
              />
            </SettingsRow>
            
            <SettingsRow 
              icon={Bell} 
              label="Messages" 
              description="New messages from contractors and support"
            >
              <Switch
                checked={settings?.notifications?.messages ?? true}
                onCheckedChange={(checked) => updateSetting('notifications.messages', checked)}
                data-testid="notif-messages"
              />
            </SettingsRow>
            
            <SettingsRow 
              icon={Mail} 
              label="Email Notifications" 
              description="Receive notifications via email"
            >
              <Switch
                checked={settings?.notifications?.email_notifications ?? true}
                onCheckedChange={(checked) => updateSetting('notifications.email_notifications', checked)}
                data-testid="notif-email"
              />
            </SettingsRow>
            
            <SettingsRow 
              icon={Bell} 
              label="Marketing" 
              description="Tips, promotions, and news"
            >
              <Switch
                checked={settings?.notifications?.marketing ?? false}
                onCheckedChange={(checked) => updateSetting('notifications.marketing', checked)}
                data-testid="notif-marketing"
              />
            </SettingsRow>
          </SettingsSection>

          {/* Preferences */}
          <SettingsSection title="Preferences" description="Customize your experience">
            <SettingsRow 
              icon={Globe} 
              label="Language" 
              description={settings?.language === 'en' ? 'English' : settings?.language}
            >
              <select
                value={settings?.language || 'en'}
                onChange={(e) => updateSetting('language', e.target.value)}
                className="px-3 py-1.5 border border-zinc-200 rounded-md text-sm"
                data-testid="language-select"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
              </select>
            </SettingsRow>
            
            <SettingsRow 
              icon={Globe} 
              label="Currency" 
              description="Default currency for quotes"
            >
              <select
                value={settings?.currency || 'GBP'}
                onChange={(e) => updateSetting('currency', e.target.value)}
                className="px-3 py-1.5 border border-zinc-200 rounded-md text-sm"
                data-testid="currency-select"
              >
                <option value="GBP">£ GBP</option>
                <option value="USD">$ USD</option>
                <option value="EUR">€ EUR</option>
              </select>
            </SettingsRow>
          </SettingsSection>

          {/* Privacy & Data */}
          <SettingsSection title="Privacy & Data" description="Manage your data and privacy">
            <SettingsRow 
              icon={Shield} 
              label="Analytics" 
              description="Help improve ZOLT with anonymous usage data"
            >
              <Switch
                checked={settings?.analytics_opt_in ?? true}
                onCheckedChange={(checked) => updateSetting('analytics_opt_in', checked)}
                data-testid="analytics-opt-in"
              />
            </SettingsRow>
            
            <SettingsRow 
              icon={Download} 
              label="Export My Data" 
              description="Download a copy of your data"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={handleDataExport}
                data-testid="export-data-btn"
              >
                Request
              </Button>
            </SettingsRow>
            
            <SettingsRow 
              icon={Trash2} 
              label="Delete Account" 
              description="Permanently delete your account and data"
            >
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setShowDeleteModal(true)}
                data-testid="delete-account-btn"
              >
                Delete
              </Button>
            </SettingsRow>
          </SettingsSection>
        </motion.div>
      </div>

      {/* Password Change Modal */}
      <AlertDialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Password</AlertDialogTitle>
            <AlertDialogDescription>
              Enter your current password and choose a new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="mt-1"
              />
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={changingPassword}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePasswordChange}
              disabled={changingPassword || !currentPassword || !newPassword}
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Modal */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All your data will be permanently deleted.
              Type <strong>DELETE</strong> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="text-center font-mono"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAccountDeletion}
              disabled={deleteConfirmText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
};

export default SettingsPage;
